import { AxiosResponse } from 'axios';
import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import { SignInResponse } from '../../api/types';
import { SimplyTestApi } from '../../api/simplytest';
import { DEFAULT_UNAUTHORIZED_MESSAGE, ROUTES } from '../../shared/constants';
import { selectConfig } from '../../shared/redux/slice';
import { IConfiguration } from '../../shared/types';
import authSlice, { confirmPasswordReset, passwordReset, selectIdToken, selectIsAuthenticated, selectRefreshToken, signIn, unauthorized } from './slice';
import { toast } from 'react-hot-toast';
import { getInfoIcon } from '../../shared/components/ToastIcons';

export abstract class AuthSagas {
    public static* run(){
        try {
            yield all([
                takeLatest(signIn.type, AuthSagas.signIn),
                takeLatest(unauthorized.type, AuthSagas.unauthorized),
                takeLatest(passwordReset, AuthSagas.resetPassword),
                takeLatest(confirmPasswordReset, AuthSagas.confirmPasswordReset),
            ]);
        }
        catch(e: any){
            console.log(`[SharedSagas:run] the following exception has occurred`, e);
        }
    }

    private static* signIn(action: ReturnType<typeof signIn>){
        const { payload: { username, password } } = action;
        try {
            yield put(authSlice.actions.setSignInState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const signInResponse: AxiosResponse<SignInResponse> = yield call(
                {
                    context: client,
                    fn: client.signIn
                },
                {
                    username,
                    password,
                }
            );

            if(signInResponse.status !== 200)
                throw new Error("Error communicating with api");

            // Sep 2026 - refresh-token flow (was plumbed through types/localStorage but never
            // actually populated until now) - lets httpClient.ts silently renew the access token
            // via POST /api/auth/refresh instead of the user hitting a hard "Session Expired" the
            // moment the 60-minute access token dies.
            const { token, refreshToken } = signInResponse.data;

            yield put(authSlice.actions.setAuth({
                token,
                refreshToken,
            }));
            
            yield put(authSlice.actions.setSignInState('READY'));
        }
        catch(e: any){
            toast.error('Incorrect username or password, please try again');
            console.log(`[SharedSagas:signIn] the following exception has occurred`, e);
        }
        finally {
            yield put(authSlice.actions.setSignInState('READY'));
        }
    }

    private static *unauthorized(action: ReturnType<typeof unauthorized>) {
        try {
            // On session expiry, every in-flight authenticated request across the app gets a 401
            // around the same time, and each one independently dispatches this same action - the
            // first one to run already clears auth, so every later one in the same burst bails
            // here instead of re-clearing auth and stacking up a duplicate "Session Expired" toast.
            const isAuthenticated: boolean = yield select(selectIsAuthenticated);
            if(!isAuthenticated)
                return;

            // Sep 2026 - "we need to fix this properly" (re: stray un-revoked RefreshTokens rows
            // piling up per user) - this is the ONE place auth actually gets cleared, whatever
            // triggered it (an explicit "Sign Out" page dispatching this directly, or a real 401
            // where httpClient.ts's own silent-refresh attempt already failed) - so it's the right
            // single place to also revoke the current refresh token server-side, rather than
            // leaving it validly usable for its full 14-day lifetime after the user is already
            // signed out client-side. Best-effort: read BEFORE clearAuth wipes it, and never let a
            // failed/offline revoke call block the actual local sign-out below.
            const refreshToken: string | undefined = yield select(selectRefreshToken);
            if (refreshToken) {
                try {
                    const config: IConfiguration = yield select(selectConfig);
                    const client = new SimplyTestApi(config.apiBaseUrl);
                    yield call({ context: client, fn: client.revoke }, { refreshToken });
                } catch (e: any) {
                    console.log(`[AuthSagas:unauthorized] revoke call failed, continuing sign-out anyway`, e);
                }
            }

            yield put(authSlice.actions.clearAuth());
            toast(action.payload?.message ?? DEFAULT_UNAUTHORIZED_MESSAGE, {
                icon: getInfoIcon()
            });

        }
        catch(e: any) {
            console.log(`[SecuritySagas:unauthorized] the following exception has occurred`, e);
        }
    }

    private static* resetPassword(action: ReturnType<typeof passwordReset>){
        const { payload: { email }} = action;
        try {

            yield put(authSlice.actions.setResetPasswordState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const resetPasswordResponse: AxiosResponse<SignInResponse> = yield call(
                {
                    context: client,
                    fn: client.resetPassword,
                },
                email
            );

            
            if(resetPasswordResponse.status !== 200)
                throw new Error("Error communicating with api");

            yield call({ context: toast, fn: toast.success}, `Your request has been sent, please check your inbox for instructions`);
        }
        catch(e: any){
            toast.error('Could not request password reset, please try again');
            console.log(`[AuthSagas:resetPassword] the following exception has occurred`, e);
        }
        finally {
            yield put(authSlice.actions.setResetPasswordState('READY'));
        }
    }

    private static* confirmPasswordReset(action: ReturnType<typeof confirmPasswordReset>){
        const { payload } = action;
        const { userId, token, password, confirmPassword, navigate } = payload;
        try {
            yield put(authSlice.actions.setResetPasswordConfirmState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);

            const resp: AxiosResponse<any> = yield call({
                context: client,
                fn: client.resetPasswordConfirm,
            },{
                password,
                confirmPassword
            },userId, token);

            if(resp.status === 200 && resp.data.status !== false) {
                yield toast.success('Your password has been changed');

                //Redirect back to signin
                yield navigate(ROUTES.SignIn);
                return;
            }

            //Display serverside error message if available
            if(resp.status === 200 && resp.data?.status === false && resp.data?.message !== undefined){
                yield toast.error(`Serverside error: ${resp.data.message}`);

                return;
            }

            //Display a generic error toast if we reached this code
            yield toast.error('Unable to process your request. Please try again.');
        }
        catch(e: any) {
            yield toast.error('Unable to process your request. Please try again.');
            console.log(`[SecuritySagas:confirmPasswordReset] the following exception has occurred`, e);
        }
        finally {
            yield put(authSlice.actions.setResetPasswordConfirmState('READY'));
        }
    }
}